<form [formGroup]="staffForm" class="atp">
  <header class="atp-head">
    <button type="button" class="atp-close" (click)="cancel()" aria-label="Close">
      <i class="bx bx-x" aria-hidden="true"></i>
    </button>
    <h2 class="atp-title">
      @if (staff) {
        Edit Staff — {{ staff.display_name || staff.first_name }}
      } @else {
        Add New Staff
      }
    </h2>
    <div class="atp-actions">
      <button type="button" class="atp-cancel" (click)="cancel()">Cancel</button>
      <button
        type="button"
        class="atp-save"
        [disabled]="staffForm.invalid || isDuplicate"
        (click)="save()"
      >
        @if (staff) {
          Save Changes
        } @else {
          Save Staff
        }
      </button>
    </div>
  </header>

  <div class="atp-body">
    <!-- Account setup -->
    <section class="atp-card">
      <header class="atp-card-head">
        <span class="atp-card-icon"><i class="bx bx-check-shield" aria-hidden="true"></i></span>
        <div class="atp-card-headings">
          <h3 class="atp-card-title">Account Setup</h3>
          <p class="atp-card-sub">
            @if (staff) {
              Set up the staff account and invitation details.
            } @else {
              Set up the staff account and send an invitation.
            }
          </p>
        </div>
      </header>

      <label class="atp-label" for="staffEmail">Staff Email *</label>
      <div class="sf-email">
        <input
          id="staffEmail"
          class="atp-input"
          type="email"
          autocomplete="off"
          placeholder="e.g. john@restaurant.com"
          formControlName="email"
          (input)="filterEmail()"
          [class.input-error]="
            isDuplicate || (staffForm.get('email')?.invalid && staffForm.get('email')?.touched)
          "
        />
        @if (staff) {
          <span class="sf-invited">
            <i class="bx bx-lock-alt" aria-hidden="true"></i>
            Invited
          </span>
        }
      </div>
      <p class="atp-help">
        @if (staff) {
          Email was set during invitation and cannot be changed
        } @else {
          An invitation will be sent to this email.
        }
      </p>
      @if (staffForm.get('email')?.touched && staffForm.get('email')?.errors?.['required']) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Email cannot be empty.</p>
        </div>
      }
      @if (staffForm.get('email')?.touched && staffForm.get('email')?.errors?.['pattern']) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Enter a valid email address.</p>
        </div>
      }
      @if (isDuplicate) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>A staff member with this email already exists.</p>
        </div>
      }
    </section>

    <!-- Personal information -->
    <section class="atp-card">
      <header class="atp-card-head">
        <span class="atp-card-icon atp-card-icon--info"
          ><i class="bx bx-user" aria-hidden="true"></i
        ></span>
        <div class="atp-card-headings">
          <h3 class="atp-card-title">Personal Information</h3>
          <p class="atp-card-sub">Basic details for the staff member.</p>
        </div>
      </header>

      <div class="sf-row">
        <div class="sf-col">
          <label class="atp-label" for="firstName">First Name *</label>
          <input
            id="firstName"
            class="atp-input"
            type="text"
            autocomplete="off"
            placeholder="e.g. John"
            formControlName="first_name"
            [class.input-error]="
              staffForm.get('first_name')?.invalid && staffForm.get('first_name')?.touched
            "
          />
        </div>
        <div class="sf-col">
          <label class="atp-label" for="lastName">Last Name *</label>
          <input
            id="lastName"
            class="atp-input"
            type="text"
            autocomplete="off"
            placeholder="e.g. Doe"
            formControlName="last_name"
            [class.input-error]="
              staffForm.get('last_name')?.invalid && staffForm.get('last_name')?.touched
            "
          />
        </div>
      </div>
      @if (
        (staffForm.get('first_name')?.touched && staffForm.get('first_name')?.invalid) ||
        (staffForm.get('last_name')?.touched && staffForm.get('last_name')?.invalid)
      ) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Names must be between 3 and 50 characters.</p>
        </div>
      }

      <label class="atp-label atp-label--spaced" for="displayName">Display Name *</label>
      <input
        id="displayName"
        class="atp-input"
        type="text"
        autocomplete="off"
        placeholder="e.g. John D."
        formControlName="display_name"
        [class.input-error]="
          staffForm.get('display_name')?.invalid && staffForm.get('display_name')?.touched
        "
      />
      <p class="atp-help">This name will be shown across the system.</p>

      <label class="atp-label atp-label--spaced" for="contactNo">Contact No *</label>
      <div
        class="phone-field"
        [class.input-error]="
          staffForm.get('phone_number')?.invalid && staffForm.get('phone_number')?.touched
        "
      >
        <span class="phone-prefix" [attr.aria-label]="phoneCountry.name">
          <span class="phone-flag" aria-hidden="true">{{ phoneFlag }}</span>
          +{{ phoneCountry.dialCode }}
        </span>
        <input
          id="contactNo"
          class="atp-input phone-input"
          type="tel"
          autocomplete="off"
          placeholder="(555) 123-4567"
          formControlName="phone_number"
          (input)="onPhoneInput($event)"
        />
      </div>
      @if (staffForm.get('phone_number')?.touched && staffForm.get('phone_number')?.invalid) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Enter a contact number as (555) 123-4567.</p>
        </div>
      }
    </section>

    <!-- Role & schedule -->
    <section class="atp-card">
      <header class="atp-card-head">
        <span class="atp-card-icon atp-card-icon--warn"
          ><i class="bx bx-briefcase" aria-hidden="true"></i
        ></span>
        <div class="atp-card-headings">
          <h3 class="atp-card-title">Role &amp; Schedule</h3>
          <p class="atp-card-sub">Assign a role and define the schedule type.</p>
        </div>
      </header>

      <label class="atp-label" for="staffRole">Role *</label>
      <select id="staffRole" class="atp-input sf-select" formControlName="user_role">
        <option value="" disabled>Select a role</option>
        @for (role of roles; track role.name) {
          <option [value]="role.name">{{ role.displayName }}</option>
        }
      </select>
      @if (staffForm.get('user_role')?.touched && staffForm.get('user_role')?.invalid) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Select a role for this staff member.</p>
        </div>
      }

      <span class="atp-label atp-label--spaced">Shift Type *</span>
      <div class="sf-shifts">
        @for (shift of shifts; track shift.name) {
          <button
            type="button"
            class="sf-shift"
            [class.sf-shift--active]="isShift(shift.name)"
            (click)="selectShift(shift)"
          >
            {{ shift.displayName }}
          </button>
        }
      </div>
      <ul class="sf-shift-hints">
        @for (shift of shifts; track shift.name) {
          <li>{{ shift.displayName }} &ndash; {{ shift.hint }}</li>
        }
      </ul>
    </section>
  </div>
</form>
